/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Tool } from '../models/Tool';
import type { ToolConfig } from '../models/ToolConfig';
import type { ToolConfigDaten } from '../models/ToolConfigDaten';
import type { ToolConfigListe } from '../models/ToolConfigListe';
import type { ToolConfigSchema } from '../models/ToolConfigSchema';
import type { ToolDaten } from '../models/ToolDaten';
import type { ToolListe } from '../models/ToolListe';
import type { ToolStage } from '../models/ToolStage';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ToolsService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Konfigurationen eines Tools für eine Aufgabe
   * Listet alle Konfigurationen eines Tools für eine Aufgabe
   * @returns ToolConfigListe Liste der Konfigurationen
   * @throws ApiError
   */
  public getToolConfigsForAufgabe({
    aufgabeId,
    toolId,
  }: {
    aufgabeId: string,
    toolId: string,
  }): CancelablePromise<ToolConfigListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/tools/{toolId}/configs',
      path: {
        'aufgabeId': aufgabeId,
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool-Konfiguration erstellen/aktualisieren
   * Erstellt oder aktualisiert einen Konfigurationswert eines Tools für eine Aufgabe
   * @returns ToolConfig Die gespeicherte Konfiguration
   * @throws ApiError
   */
  public upsertToolConfig({
    aufgabeId,
    toolId,
    key,
    requestBody,
  }: {
    aufgabeId: string,
    toolId: string,
    key: string,
    requestBody: ToolConfigDaten,
  }): CancelablePromise<ToolConfig> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/aufgaben/{aufgabeId}/tools/{toolId}/configs/{key}',
      path: {
        'aufgabeId': aufgabeId,
        'toolId': toolId,
        'key': key,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool-Konfiguration löschen
   * Löscht eine Konfiguration eines Tools für eine Aufgabe
   * @returns ToolConfig Die gelöschte Konfiguration
   * @throws ApiError
   */
  public deleteToolConfig({
    aufgabeId,
    toolId,
    key,
  }: {
    aufgabeId: string,
    toolId: string,
    key: string,
  }): CancelablePromise<ToolConfig> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/aufgaben/{aufgabeId}/tools/{toolId}/configs/{key}',
      path: {
        'aufgabeId': aufgabeId,
        'toolId': toolId,
        'key': key,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tools suchen
   * Sucht nach verfügbaren Tools
   * @returns ToolListe Liste der Tools
   * @throws ApiError
   */
  public searchTools({
    search,
    stage,
    mandantId,
    page,
    limit,
    sort,
  }: {
    search?: string,
    stage?: ToolStage,
    /**
     * Wenn gesetzt, werden nur globale Tools und Tools dieses Mandanten zurückgegeben.
     */
    mandantId?: string,
    page?: number,
    limit?: number,
    sort?: string,
  }): CancelablePromise<ToolListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/tools',
      query: {
        'search': search,
        'stage': stage,
        'mandantId': mandantId,
        'page': page,
        'limit': limit,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Ein Tool bekommen
   * Bekomme ein Tool
   * @returns Tool Das Tool
   * @throws ApiError
   */
  public getTool({
    toolId,
  }: {
    toolId: string,
  }): CancelablePromise<Tool> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/tools/{toolId}',
      path: {
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool-Verfügbarkeit aktualisieren
   * Aktualisiert die Verfügbarkeit (Ownership) eines Tools
   * @returns Tool Das aktualisierte Tool
   * @throws ApiError
   */
  public updateTool({
    toolId,
    requestBody,
  }: {
    toolId: string,
    requestBody: ToolDaten,
  }): CancelablePromise<Tool> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/tools/{toolId}',
      path: {
        'toolId': toolId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Konfigurationsschema eines Tools
   * Liefert das Konfigurationsschema eines Tools
   * @returns ToolConfigSchema Das Konfigurationsschema
   * @throws ApiError
   */
  public getToolConfigSchema({
    toolId,
  }: {
    toolId: string,
  }): CancelablePromise<ToolConfigSchema> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/tools/{toolId}/config-schema',
      path: {
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

}
